import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

export default function Clock() {
  const { profile } = useAuth()
  const isAdmin = profile?.role === 'admin'
  const [tab, setTab] = useState('my')

  // --- My Clock State ---
  const [activeEntry, setActiveEntry] = useState(null)
  const [jobs, setJobs] = useState([])
  const [selectedJob, setSelectedJob] = useState('')
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [elapsed, setElapsed] = useState(0)
  const [onBreak, setOnBreak] = useState(false)
  const [breakStart, setBreakStart] = useState(null)
  const [breakElapsed, setBreakElapsed] = useState(0)
  const [showSwitchJob, setShowSwitchJob] = useState(false)
  const [switchingJob, setSwitchingJob] = useState(false)

  // --- Admin Time Cards State ---
  const [allEntries, setAllEntries] = useState([])
  const [allJobs, setAllJobs] = useState([])
  const [employees, setEmployees] = useState([])
  const [filterEmployee, setFilterEmployee] = useState('')
  const [filterJob, setFilterJob] = useState('')
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  const [filterPreset, setFilterPreset] = useState('this_week')
  const [adminLoading, setAdminLoading] = useState(false)
  const [editingEntry, setEditingEntry] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [showAddModal, setShowAddModal] = useState(false)
  const [addForm, setAddForm] = useState({ employee_id: '', job_id: '', clock_in: '', clock_out: '' })
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [copySource, setCopySource] = useState(null)
  const [copyEmployees, setCopyEmployees] = useState([])
  const [saving, setSaving] = useState(false)
  const [copySaving, setCopySaving] = useState(false)

  useEffect(() => { if (profile?.id) fetchData() }, [profile?.id])
  useEffect(() => { if (isAdmin && tab === 'admin') fetchAdminData() }, [tab])

  // Apply preset date ranges
  useEffect(() => {
    if (filterPreset === 'custom') return
    const now = new Date()
    const monday = (d) => {
      const day = d.getDay()
      const diff = d.getDate() - day + (day === 0 ? -6 : 1)
      return new Date(d.setDate(diff))
    }
    const fmt = (d) => d.toISOString().split('T')[0]
    if (filterPreset === 'this_week') {
      const start = monday(new Date())
      const end = new Date()
      setFilterFrom(fmt(start))
      setFilterTo(fmt(end))
    } else if (filterPreset === 'last_week') {
      const start = monday(new Date())
      start.setDate(start.getDate() - 7)
      const end = new Date(start)
      end.setDate(end.getDate() + 6)
      setFilterFrom(fmt(start))
      setFilterTo(fmt(end))
    } else if (filterPreset === 'this_month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      setFilterFrom(fmt(start))
      setFilterTo(fmt(new Date()))
    } else if (filterPreset === 'last_month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      setFilterFrom(fmt(start))
      setFilterTo(fmt(end))
    }
  }, [filterPreset])

  useEffect(() => {
    if (!activeEntry) return
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - new Date(activeEntry.clock_in)) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [activeEntry])

  useEffect(() => {
    if (!onBreak || !breakStart) return
    const interval = setInterval(() => {
      setBreakElapsed(Math.floor((Date.now() - breakStart) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [onBreak, breakStart])

  async function fetchData() {
    const [activeRes, jobsRes] = await Promise.all([
      supabase.from('time_entries').select('*, job:jobs(title)').eq('employee_id', profile?.id).is('clock_out', null).maybeSingle(),
      supabase.from('jobs').select('id, title, client:clients(name)').in('status', ['active', 'pending']).order('created_at', { ascending: false }),
    ])
    if (activeRes.data) {
      setActiveEntry(activeRes.data)
      setElapsed(Math.floor((Date.now() - new Date(activeRes.data.clock_in)) / 1000))
    }
    setJobs(jobsRes.data || [])
    setLoading(false)
  }

  async function fetchAdminData() {
    setAdminLoading(true)
    const [entriesRes, empRes, allJobsRes] = await Promise.all([
      supabase.from('time_entries')
        .select('*, job:jobs(title), employee:profiles(full_name, id)')
        .order('clock_in', { ascending: false })
        .limit(500),
      supabase.from('profiles').select('id, full_name').order('full_name'),
      supabase.from('jobs').select('id, title, status, client:clients(name)').order('created_at', { ascending: false }),
    ])
    setAllEntries(entriesRes.data || [])
    setEmployees(empRes.data || [])
    setAllJobs(allJobsRes.data || [])
    setAdminLoading(false)
  }

  function getLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) { reject('Geolocation not supported'); return }
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        err => reject(err.message),
        { enableHighAccuracy: true, timeout: 10000 }
      )
    })
  }

  async function clockIn() {
    if (!selectedJob) { alert('Please select a job first'); return }
    setWorking(true)
    setLocationError('')
    let gps = null
    try { gps = await getLocation() } catch (e) { setLocationError('Location unavailable: ' + e) }
    const { data, error } = await supabase.from('time_entries').insert([{
      employee_id: profile?.id,
      job_id: selectedJob,
      clock_in: new Date().toISOString(),
      gps_clock_in_lat: gps?.lat ?? null,
      gps_clock_in_lng: gps?.lng ?? null,
    }]).select('*, job:jobs(title)').maybeSingle()
    if (error) { alert('Clock in failed: ' + error.message); setWorking(false); return }
    setActiveEntry(data)
    setElapsed(0)
    setWorking(false)
    fetchData()
  }

  async function clockOut() {
    setWorking(true)
    let gps = null
    try { gps = await getLocation() } catch (e) {}
    await supabase.from('time_entries').update({
      clock_out: new Date().toISOString(),
      gps_clock_out_lat: gps?.lat ?? null,
      gps_clock_out_lng: gps?.lng ?? null,
    }).eq('id', activeEntry.id)
    setActiveEntry(null)
    setElapsed(0)
    setOnBreak(false)
    setBreakStart(null)
    setBreakElapsed(0)
    setWorking(false)
    fetchData()
  }

  function startBreak() { setOnBreak(true); setBreakStart(Date.now()); setBreakElapsed(0) }
  function endBreak() { setOnBreak(false); setBreakStart(null); setBreakElapsed(0) }

  async function switchJob(newJobId) {
    if (!newJobId || newJobId === activeEntry?.job_id) { setShowSwitchJob(false); return }
    setSwitchingJob(true)
    let gps = null
    try { gps = await getLocation() } catch (e) {}
    await supabase.from('time_entries').update({
      clock_out: new Date().toISOString(),
      gps_clock_out_lat: gps?.lat ?? null,
      gps_clock_out_lng: gps?.lng ?? null,
    }).eq('id', activeEntry.id)
    const { data, error } = await supabase.from('time_entries').insert([{
      employee_id: profile?.id,
      job_id: newJobId,
      clock_in: new Date().toISOString(),
      gps_clock_in_lat: gps?.lat ?? null,
      gps_clock_in_lng: gps?.lng ?? null,
    }]).select('*, job:jobs(title)').maybeSingle()
    if (error) { alert('Switch failed: ' + error.message); setSwitchingJob(false); return }
    setActiveEntry(data)
    setElapsed(0)
    setOnBreak(false)
    setBreakStart(null)
    setBreakElapsed(0)
    setShowSwitchJob(false)
    setSwitchingJob(false)
    fetchData()
  }

  function formatElapsed(secs) {
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  function formatDuration(entry) {
    if (!entry.clock_out) return '—'
    const secs = Math.floor((new Date(entry.clock_out) - new Date(entry.clock_in)) / 1000)
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    return `${h}h ${m}m`
  }

  function toLocalInput(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  function mapsLink(lat, lng) {
    return `https://www.google.com/maps?q=${lat},${lng}`
  }

  const filteredEntries = allEntries.filter(e => {
    if (filterEmployee && e.employee_id !== filterEmployee) return false
    if (filterJob && e.job_id !== filterJob) return false
    if (filterFrom && new Date(e.clock_in) < new Date(filterFrom)) return false
    if (filterTo && new Date(e.clock_in) > new Date(filterTo + 'T23:59:59')) return false
    return true
  })

  const grouped = filteredEntries.reduce((acc, entry) => {
    const day = new Date(entry.clock_in).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    if (!acc[day]) acc[day] = []
    acc[day].push(entry)
    return acc
  }, {})

  const uniqueFilterJobs = [...new Map(allEntries.filter(e => e.job).map(e => [e.job_id, e.job])).values()]

  const totalSeconds = filteredEntries.reduce((sum, e) => {
    if (!e.clock_out) return sum
    return sum + Math.floor((new Date(e.clock_out) - new Date(e.clock_in)) / 1000)
  }, 0)
  const totalHours = (totalSeconds / 3600).toFixed(1)

  function openEdit(entry) {
    setEditingEntry(entry)
    setEditForm({
      clock_in: toLocalInput(entry.clock_in),
      clock_out: toLocalInput(entry.clock_out),
      job_id: entry.job_id,
      employee_id: entry.employee_id,
    })
  }

  async function saveEdit() {
    setSaving(true)
    const { error } = await supabase.from('time_entries').update({
      clock_in: new Date(editForm.clock_in).toISOString(),
      clock_out: editForm.clock_out ? new Date(editForm.clock_out).toISOString() : null,
      job_id: editForm.job_id || null,
      employee_id: editForm.employee_id,
    }).eq('id', editingEntry.id)
    if (error) { alert('Save failed: ' + error.message) }
    setSaving(false)
    setEditingEntry(null)
    fetchAdminData()
  }

  async function deleteEntry(id) {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return
    const { error } = await supabase.from('time_entries').delete().eq('id', id)
    if (error) { alert('Delete failed: ' + error.message) }
    fetchAdminData()
  }

  async function saveAdd() {
    if (!addForm.employee_id || !addForm.job_id || !addForm.clock_in) {
      alert('Employee, job, and clock-in time are required.')
      return
    }
    setSaving(true)
    const { error } = await supabase.from('time_entries').insert([{
      employee_id: addForm.employee_id,
      job_id: addForm.job_id,
      clock_in: new Date(addForm.clock_in).toISOString(),
      clock_out: addForm.clock_out ? new Date(addForm.clock_out).toISOString() : null,
    }])
    if (error) { alert('Add failed: ' + error.message) }
    setSaving(false)
    setShowAddModal(false)
    setAddForm({ employee_id: '', job_id: '', clock_in: '', clock_out: '' })
    fetchAdminData()
  }

  function openCopy(entry) {
    setCopySource(entry)
    setCopyEmployees([])
    setShowCopyModal(true)
  }

  async function saveCopy() {
    if (copyEmployees.length === 0) { alert('Select at least one employee to copy to.'); return }
    setCopySaving(true)
    const inserts = copyEmployees.map(empId => ({
      employee_id: empId,
      job_id: copySource.job_id,
      clock_in: copySource.clock_in,
      clock_out: copySource.clock_out || null,
    }))
    const { error } = await supabase.from('time_entries').insert(inserts)
    if (error) { alert('Copy failed: ' + error.message) }
    setCopySaving(false)
    setShowCopyModal(false)
    setCopySource(null)
    setCopyEmployees([])
    fetchAdminData()
  }

  function toggleCopyEmployee(id) {
    setCopyEmployees(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id])
  }

  if (loading) return <div style={styles.loading}>Loading...</div>

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Time Clock</h1>

      <div style={styles.tabs}>
        <button style={tab === 'my' ? styles.tabActive : styles.tab} onClick={() => setTab('my')}>My Clock</button>
        {isAdmin && <button style={tab === 'admin' ? styles.tabActive : styles.tab} onClick={() => setTab('admin')}>Time Cards</button>}
      </div>

      {/* ── MY CLOCK TAB ── */}
      {tab === 'my' && (
        <>
          {activeEntry ? (
            <div style={styles.activeCard}>
              <div style={styles.activeTop}>
                <div style={{ ...styles.activeDot, background: onBreak ? '#d97706' : '#4ade80' }} />
                <span style={styles.activeLabel}>{onBreak ? '☕ On Break' : 'Currently Clocked In'}</span>
              </div>
              <div style={styles.jobName}>{activeEntry.job?.title}</div>
              <div style={{ ...styles.timer, color: onBreak ? '#d97706' : '#4ade80' }}>{formatElapsed(elapsed)}</div>
              {onBreak && <div style={styles.breakTimer}>Break time: {formatElapsed(breakElapsed)}</div>}
              <div style={styles.clockInTime}>Started: {new Date(activeEntry.clock_in).toLocaleTimeString()}</div>

              {showSwitchJob && (
                <div style={styles.switchJobBox}>
                  <label style={styles.switchJobLabel}>Switch to job:</label>
                  <select style={styles.jobSelect} defaultValue="" onChange={e => switchJob(e.target.value)} disabled={switchingJob}>
                    <option value="">Select new job...</option>
                    {jobs.filter(j => j.id !== activeEntry?.job_id).map(j => (
                      <option key={j.id} value={j.id}>{j.title}{j.client ? ` — ${j.client.name}` : ''}</option>
                    ))}
                  </select>
                  <button style={styles.cancelSwitchBtn} onClick={() => setShowSwitchJob(false)}>Cancel</button>
                </div>
              )}

              <div style={styles.activeButtons}>
                {!onBreak ? (
                  <button style={styles.breakBtn} onClick={startBreak} disabled={working}>☕ Break</button>
                ) : (
                  <button style={styles.resumeBtn} onClick={endBreak} disabled={working}>▶ Resume</button>
                )}
                <button style={styles.switchBtn} onClick={() => setShowSwitchJob(v => !v)} disabled={working || switchingJob}>
                  🔄 Switch Job
                </button>
              </div>
              <button style={styles.clockOutBtn} onClick={clockOut} disabled={working}>
                {working ? 'Processing...' : '⏹ Clock Out'}
              </button>
            </div>
          ) : (
            <div style={styles.clockInCard}>
              <h2 style={styles.cardTitle}>Select Job to Clock In</h2>
              {jobs.length === 0 ? (
                <p style={styles.noJobsMsg}>No active jobs assigned. Contact your manager.</p>
              ) : (
                <select style={styles.jobSelect} value={selectedJob} onChange={e => setSelectedJob(e.target.value)}>
                  <option value="">Choose a job...</option>
                  {jobs.map(j => (
                    <option key={j.id} value={j.id}>{j.title}{j.client ? ` — ${j.client.name}` : ''}</option>
                  ))}
                </select>
              )}
              {locationError && <div style={styles.locationWarn}>⚠️ {locationError}</div>}
              <button style={{ ...styles.clockInBtn, opacity: (!selectedJob || working) ? 0.6 : 1 }} onClick={clockIn} disabled={working || !selectedJob}>
                {working ? 'Getting location...' : '▶ Clock In'}
              </button>
              <p style={styles.locationNote}>📍 GPS location recorded automatically</p>
            </div>
          )}
        </>
      )}

      {/* ── ADMIN TIME CARDS TAB ── */}
      {tab === 'admin' && isAdmin && (
        <div>
          {/* Preset filter buttons */}
          <div style={styles.presetBar}>
            {[
              { key: 'this_week', label: 'This Week' },
              { key: 'last_week', label: 'Last Week' },
              { key: 'this_month', label: 'This Month' },
              { key: 'last_month', label: 'Last Month' },
              { key: 'custom', label: 'Custom' },
            ].map(p => (
              <button
                key={p.key}
                style={filterPreset === p.key ? styles.presetBtnActive : styles.presetBtn}
                onClick={() => setFilterPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Secondary filters */}
          <div style={styles.filterBar}>
            <select style={styles.filterSelect} value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
              <option value="">All Employees</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
            </select>
            <select style={styles.filterSelect} value={filterJob} onChange={e => setFilterJob(e.target.value)}>
              <option value="">All Jobs</option>
              {uniqueFilterJobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            {filterPreset === 'custom' && (
              <>
                <input type="date" style={styles.filterInput} value={filterFrom} onChange={e => setFilterFrom(e.target.value)} placeholder="From" />
                <input type="date" style={styles.filterInput} value={filterTo} onChange={e => setFilterTo(e.target.value)} placeholder="To" />
              </>
            )}
            <button style={styles.clearBtn} onClick={() => { setFilterEmployee(''); setFilterJob(''); setFilterPreset('this_week') }}>Clear</button>
            <div style={{ marginLeft: 'auto' }}>
              <button style={styles.addBtn} onClick={() => setShowAddModal(true)}>+ Add Entry</button>
            </div>
          </div>

          {/* Summary bar */}
          <div style={styles.summaryBar}>
            <span style={styles.summaryText}>{filteredEntries.length} entries</span>
            <span style={styles.summaryText}>Total: <strong>{totalHours} hrs</strong></span>
          </div>

          {adminLoading ? (
            <div style={styles.loading}>Loading time cards...</div>
          ) : filteredEntries.length === 0 ? (
            <div style={styles.noData}>No time entries found for this period.</div>
          ) : (
            Object.entries(grouped).map(([day, entries]) => {
              const daySeconds = entries.reduce((sum, e) => {
                if (!e.clock_out) return sum
                return sum + Math.floor((new Date(e.clock_out) - new Date(e.clock_in)) / 1000)
              }, 0)
              const dayHours = (daySeconds / 3600).toFixed(1)
              return (
                <div key={day} style={styles.dayGroup}>
                  <div style={styles.dayHeader}>
                    <span style={styles.dayLabel}>{day}</span>
                    <span style={styles.dayTotal}>{dayHours} hrs</span>
                  </div>
                  {entries.map(entry => (
                    <div key={entry.id} style={styles.adminRow}>
                      <div style={styles.adminLeft}>
                        <span style={styles.adminName}>{entry.employee?.full_name || '—'}</span>
                        <span style={styles.adminJobTitle}>{entry.job?.title || '—'}</span>
                      </div>
                      <div style={styles.adminMiddle}>
                        <span style={styles.adminTime}>{new Date(entry.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <span style={styles.adminArrow}>→</span>
                        <span style={styles.adminTime}>
                          {entry.clock_out
                            ? new Date(entry.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : <span style={styles.activeBadge}>Active</span>}
                        </span>
                        <span style={styles.adminDuration}>{formatDuration(entry)}</span>
                        {/* GPS pin — shows if clock-in location exists */}
                        {entry.gps_clock_in_lat && entry.gps_clock_in_lng && (
                          <a
                            href={mapsLink(entry.gps_clock_in_lat, entry.gps_clock_in_lng)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View clock-in location"
                            style={styles.gpsPin}
                          >
                            📍
                          </a>
                        )}
                      </div>
                      <div style={styles.adminActions}>
                        <button style={styles.copyBtn} onClick={() => openCopy(entry)} title="Copy entry for other employees">⧉ Copy</button>
                        <button style={styles.editBtn} onClick={() => openEdit(entry)}>Edit</button>
                        <button style={styles.deleteBtn} onClick={() => deleteEntry(entry.id)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      {editingEntry && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Edit Time Entry</h2>
            <label style={styles.label}>Employee</label>
            <select style={styles.modalInput} value={editForm.employee_id} onChange={e => setEditForm({ ...editForm, employee_id: e.target.value })}>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
            </select>
            <label style={styles.label}>Job</label>
            <select style={styles.modalInput} value={editForm.job_id} onChange={e => setEditForm({ ...editForm, job_id: e.target.value })}>
              <option value="">No job</option>
              {allJobs.map(j => <option key={j.id} value={j.id}>{j.title}{j.client ? ` — ${j.client.name}` : ''} ({j.status})</option>)}
            </select>
            <label style={styles.label}>Clock In</label>
            <input type="datetime-local" style={styles.modalInput} value={editForm.clock_in} onChange={e => setEditForm({ ...editForm, clock_in: e.target.value })} />
            <label style={styles.label}>Clock Out</label>
            <input type="datetime-local" style={styles.modalInput} value={editForm.clock_out} onChange={e => setEditForm({ ...editForm, clock_out: e.target.value })} />
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setEditingEntry(null)}>Cancel</button>
              <button style={styles.saveBtn} onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD ENTRY MODAL ── */}
      {showAddModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Add Time Entry</h2>
            <label style={styles.label}>Employee *</label>
            <select style={styles.modalInput} value={addForm.employee_id} onChange={e => setAddForm({ ...addForm, employee_id: e.target.value })}>
              <option value="">Select employee...</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
            </select>
            <label style={styles.label}>Job *</label>
            <select style={styles.modalInput} value={addForm.job_id} onChange={e => setAddForm({ ...addForm, job_id: e.target.value })}>
              <option value="">Select job...</option>
              {allJobs.map(j => <option key={j.id} value={j.id}>{j.title}{j.client ? ` — ${j.client.name}` : ''} ({j.status})</option>)}
            </select>
            <label style={styles.label}>Clock In *</label>
            <input type="datetime-local" style={styles.modalInput} value={addForm.clock_in} onChange={e => setAddForm({ ...addForm, clock_in: e.target.value })} />
            <label style={styles.label}>Clock Out</label>
            <input type="datetime-local" style={styles.modalInput} value={addForm.clock_out} onChange={e => setAddForm({ ...addForm, clock_out: e.target.value })} />
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => { setShowAddModal(false); setAddForm({ employee_id: '', job_id: '', clock_in: '', clock_out: '' }) }}>Cancel</button>
              <button style={styles.saveBtn} onClick={saveAdd} disabled={saving}>{saving ? 'Saving...' : 'Add Entry'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── COPY ENTRY MODAL ── */}
      {showCopyModal && copySource && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Copy Time Entry</h2>
            <p style={styles.copyInfo}>
              <strong>{copySource.job?.title || 'Unknown job'}</strong><br />
              {new Date(copySource.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' → '}
              {copySource.clock_out ? new Date(copySource.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Active'}
              {' · '}{formatDuration(copySource)}
            </p>
            <label style={styles.label}>Select employees to copy this entry to:</label>
            <div style={styles.copyEmployeeList}>
              {employees.filter(e => e.id !== copySource.employee_id).map(emp => (
                <label key={emp.id} style={styles.copyEmployeeRow}>
                  <input
                    type="checkbox"
                    checked={copyEmployees.includes(emp.id)}
                    onChange={() => toggleCopyEmployee(emp.id)}
                    style={{ marginRight: '10px', width: '16px', height: '16px' }}
                  />
                  {emp.full_name}
                </label>
              ))}
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => { setShowCopyModal(false); setCopySource(null); setCopyEmployees([]) }}>Cancel</button>
              <button style={styles.saveBtn} onClick={saveCopy} disabled={copySaving || copyEmployees.length === 0}>
                {copySaving ? 'Copying...' : `Copy to ${copyEmployees.length} employee${copyEmployees.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  page: { maxWidth: '860px', margin: '0 auto' },
  title: { margin: '0 0 16px', fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },

  tabs: { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' },
  tab: { background: 'none', border: 'none', padding: '10px 20px', fontSize: '15px', fontWeight: '600', color: '#6b7c6b', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { background: 'none', border: 'none', padding: '10px 20px', fontSize: '15px', fontWeight: '600', color: '#1a2e1a', cursor: 'pointer', borderBottom: '2px solid #2d4a2d', marginBottom: '-2px' },

  // My Clock
  activeCard: { background: 'linear-gradient(135deg, #1a2e1a, #2d4a2d)', borderRadius: '16px', padding: '32px', color: 'white', marginBottom: '24px', textAlign: 'center' },
  activeTop: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' },
  activeDot: { width: '10px', height: '10px', borderRadius: '50%', background: '#4ade80' },
  activeLabel: { fontSize: '14px', color: '#9dc99d', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' },
  jobName: { fontSize: '22px', fontWeight: '700', marginBottom: '16px' },
  timer: { fontSize: '48px', fontWeight: '700', fontFamily: 'monospace', letterSpacing: '2px', marginBottom: '8px' },
  clockInTime: { fontSize: '13px', color: '#9dc99d', marginBottom: '24px' },
  breakTimer: { fontSize: '16px', color: '#d97706', fontWeight: '600', marginBottom: '8px' },
  activeButtons: { display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', justifyContent: 'center' },
  breakBtn: { flex: 1, padding: '12px', background: '#fef3c7', border: '2px solid #d97706', color: '#92400e', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  resumeBtn: { flex: 1, padding: '12px', background: '#dcfce7', border: '2px solid #16a34a', color: '#14532d', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  switchBtn: { flex: 1, padding: '12px', background: '#eff6ff', border: '2px solid #2563eb', color: '#1e3a8a', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  clockOutBtn: { background: '#dc2626', color: 'white', border: 'none', padding: '16px 40px', borderRadius: '12px', fontSize: '18px', fontWeight: '700', cursor: 'pointer', width: '100%', marginTop: '4px' },
  switchJobBox: { background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '14px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
  switchJobLabel: { fontSize: '13px', fontWeight: '600', color: '#d1fae5' },
  cancelSwitchBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', alignSelf: 'flex-start' },

  clockInCard: { background: 'white', borderRadius: '16px', padding: '32px', marginBottom: '24px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
  cardTitle: { margin: '0 0 20px', fontSize: '20px', fontWeight: '700', color: '#1a2e1a' },
  jobSelect: { width: '100%', padding: '14px 16px', borderRadius: '10px', border: '1.5px solid #d1d5db', fontSize: '16px', fontFamily: 'inherit', marginBottom: '16px', boxSizing: 'border-box' },
  noJobsMsg: { color: '#6b7c6b', fontSize: '15px', margin: '0 0 16px', padding: '16px', background: '#f9fafb', borderRadius: '8px' },
  locationWarn: { background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' },
  clockInBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '16px', borderRadius: '12px', fontSize: '18px', fontWeight: '700', cursor: 'pointer', width: '100%' },
  locationNote: { fontSize: '12px', color: '#9ca3af', textAlign: 'center', margin: '12px 0 0' },

  // Time Cards
  presetBar: { display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' },
  presetBtn: { padding: '8px 16px', borderRadius: '20px', border: '1.5px solid #d1d5db', background: 'white', fontSize: '13px', fontWeight: '600', color: '#6b7c6b', cursor: 'pointer' },
  presetBtnActive: { padding: '8px 16px', borderRadius: '20px', border: '1.5px solid #2d4a2d', background: '#2d4a2d', fontSize: '13px', fontWeight: '600', color: 'white', cursor: 'pointer' },
  filterBar: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', alignItems: 'center' },
  filterSelect: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', background: 'white' },
  filterInput: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit' },
  clearBtn: { padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', background: 'white', fontSize: '14px', cursor: 'pointer', color: '#6b7c6b' },
  addBtn: { padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#2d4a2d', color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  summaryBar: { display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#f0f4f0', borderRadius: '8px', marginBottom: '16px' },
  summaryText: { fontSize: '14px', color: '#374151' },
  noData: { color: '#6b7c6b', fontSize: '14px', padding: '20px 0' },
  dayGroup: { marginBottom: '20px' },
  dayHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#1a2e1a', borderRadius: '8px 8px 0 0' },
  dayLabel: { color: 'white', fontWeight: '700', fontSize: '14px' },
  dayTotal: { color: '#4ade80', fontWeight: '700', fontSize: '14px' },
  adminRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'white', borderBottom: '1px solid #f3f4f6', gap: '8px', flexWrap: 'wrap' },
  adminLeft: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '140px' },
  adminName: { fontWeight: '700', fontSize: '14px', color: '#1a2e1a' },
  adminJobTitle: { fontSize: '12px', color: '#6b7c6b' },
  adminMiddle: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', flex: 1, justifyContent: 'center' },
  adminTime: { color: '#374151' },
  adminArrow: { color: '#9ca3af' },
  adminDuration: { fontWeight: '700', color: '#2d4a2d', marginLeft: '4px' },
  activeBadge: { background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '700' },
  gpsPin: { fontSize: '14px', textDecoration: 'none', marginLeft: '4px', cursor: 'pointer' },
  adminActions: { display: 'flex', gap: '6px' },
  copyBtn: { padding: '6px 12px', borderRadius: '6px', border: '1.5px solid #7c3aed', background: 'white', color: '#7c3aed', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  editBtn: { padding: '6px 14px', borderRadius: '6px', border: '1.5px solid #2d4a2d', background: 'white', color: '#2d4a2d', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  deleteBtn: { padding: '6px 14px', borderRadius: '6px', border: '1.5px solid #dc2626', background: 'white', color: '#dc2626', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },

  // Modals
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal: { background: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { margin: '0 0 8px', fontSize: '20px', fontWeight: '700', color: '#1a2e1a' },
  copyInfo: { background: '#f0f4f0', borderRadius: '8px', padding: '12px', fontSize: '14px', color: '#374151', margin: '0 0 8px', lineHeight: '1.6' },
  label: { fontSize: '13px', fontWeight: '600', color: '#374151', marginTop: '8px' },
  modalInput: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box', marginTop: '4px' },
  copyEmployeeList: { display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px', maxHeight: '200px', overflowY: 'auto', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '8px' },
  copyEmployeeRow: { display: 'flex', alignItems: 'center', padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: '#1a2e1a', fontWeight: '500' },
  modalActions: { display: 'flex', gap: '10px', marginTop: '20px' },
  cancelBtn: { flex: 1, padding: '12px', borderRadius: '10px', border: '1.5px solid #d1d5db', background: 'white', fontSize: '15px', fontWeight: '600', cursor: 'pointer', color: '#374151' },
  saveBtn: { flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: '#2d4a2d', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
}
